// src/telemetry/ws_metrics.ts
// WS metrics — tracks WebSocket connection health.

import { EventEmitter } from 'eventemitter3';
import type { ConnectionStatus } from '../api/wsClient';

export interface WSMetricsSnapshot {
  reconnectCount: number;
  heartbeatLatencyMs: number;
  disconnectDurationMs: number;
  totalDisconnectTimeMs: number;
  connectionUptimeMs: number;
  lastConnectedTs: number;
  lastDisconnectedTs: number;
  currentStatus: ConnectionStatus;
}

export class WSMetrics extends EventEmitter {
  private reconnectCount = 0;
  private heartbeatLatencyMs = 0;
  private totalDisconnectTimeMs = 0;
  private connectionUptimeMs = 0;
  private lastConnectedTs = 0;
  private lastDisconnectedTs = 0;
  private currentStatus: ConnectionStatus = 'disconnected';
  private connectionStartTs = 0;
  private disconnectStartTs = 0;

  onStatusChange(status: ConnectionStatus): void {
    const prev = this.currentStatus;
    this.currentStatus = status;

    if (prev === 'connected' && status === 'disconnected') {
      this.disconnectStartTs = Date.now();
      this.lastDisconnectedTs = Date.now();
    } else if (prev === 'disconnected' && status === 'connected') {
      if (this.disconnectStartTs > 0) {
        this.totalDisconnectTimeMs += Date.now() - this.disconnectStartTs;
        this.disconnectStartTs = 0;
      }
      this.connectionStartTs = Date.now();
      this.lastConnectedTs = Date.now();
      this.reconnectCount++;
    } else if (status === 'connected' && prev !== 'connected') {
      this.connectionStartTs = Date.now();
      this.lastConnectedTs = Date.now();
    }

    this.emit('status', this.getSnapshot());
  }

  onHeartbeat(latencyMs: number): void {
    this.heartbeatLatencyMs = latencyMs;
    this.emit('heartbeat', this.getSnapshot());
  }

  reset(): void {
    this.reconnectCount = 0;
    this.heartbeatLatencyMs = 0;
    this.totalDisconnectTimeMs = 0;
    this.connectionUptimeMs = 0;
    this.lastConnectedTs = 0;
    this.lastDisconnectedTs = 0;
    this.connectionStartTs = 0;
    this.disconnectStartTs = 0;
    this.currentStatus = 'disconnected';
    this.emit('reset', this.getSnapshot());
  }

  getSnapshot(): WSMetricsSnapshot {
    let currentDisconnectDuration = 0;
    if (this.disconnectStartTs > 0) {
      currentDisconnectDuration = Date.now() - this.disconnectStartTs;
    }

    let uptime = this.connectionUptimeMs;
    if (this.connectionStartTs > 0 && this.currentStatus === 'connected') {
      uptime += Date.now() - this.connectionStartTs;
    }

    return {
      reconnectCount: this.reconnectCount,
      heartbeatLatencyMs: this.heartbeatLatencyMs,
      disconnectDurationMs: currentDisconnectDuration,
      totalDisconnectTimeMs: this.totalDisconnectTimeMs + currentDisconnectDuration,
      connectionUptimeMs: uptime,
      lastConnectedTs: this.lastConnectedTs,
      lastDisconnectedTs: this.lastDisconnectedTs,
      currentStatus: this.currentStatus,
    };
  }
}
